import { Trans, t } from '@lingui/macro'
import { Formik } from 'formik'
import { useContext, useState } from 'react'
import * as Yup from 'yup'

import { SearchCombobox } from '@/@bothrs-ui/web/components/forms/search-combobox'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { mixpanel } from '@/mixpanel'
import {
  type DetailInputMode,
  StructuredObservation,
  useStructuredObservationDialog,
} from '@/modules/questionnaire/components/question-observation/structured-observation/StructuredObservation'
import { useMedicationSearch } from '@/modules/questionnaire/hooks/useMedicationSearch'
import { useQuestionnaireTrackingProps } from '@/modules/questionnaire/hooks/useQuestionnaireTrackingProps'
import { getDefaultDosageRows, getUnitOptions } from '../MedicationDosage.constants'
import { MedicationContext } from '../MedicationQuestion.context'
import {
  formValuesToMedicationStatement,
  isMedicationSaveDisabled,
  type MedicationFormValues,
  medicationStatementToFormValues,
  resolveDosageMode,
} from '../MedicationQuestion.helpers'
import { DOSAGE_INPUT_MIN_HEIGHT, MedicationDosageStructuredInput } from './MedicationDosageStructuredInput'

import type { MedicationSearchResult, MedicationStatement } from '@/modules/app/types/HealthParameter.types'
import type { SelectedMedication } from '../MedicationQuestion.context'

// ================================ Validation ================================

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required(
      t({
        id: 'medication.form.name.required',
        message: 'Medicatienaam is verplicht',
      }),
    )
    .max(
      100,
      t({
        id: 'medication.form.name.maxLength',
        message: 'Naam mag maximaal 100 tekens bevatten',
      }),
    ),
  dosageInstructions: Yup.string().max(
    500,
    t({
      id: 'medication.form.dosageInstructions.maxLength',
      message: 'Beschrijf hoeveel en wanneer je je medicatie inneemt (max 500 tekens)',
    }),
  ),
})

// ================================ Component ================================

export const MedicationItemForm = () => {
  const { medications, addMedication, editMedication } = useContext(MedicationContext)!
  const { dialog, closeDialog } = useStructuredObservationDialog()

  const medicationToEdit =
    dialog.mode === 'update' ? (medications.find((m) => m._clientId === dialog.itemId) ?? null) : null

  const initialSelected: SelectedMedication | null = medicationToEdit ? { name: medicationToEdit.name } : null

  const [selectedMedication, setSelectedMedication] = useState<SelectedMedication | null>(initialSelected)

  const [searchQuery, setSearchQuery] = useState('')
  const { results } = useMedicationSearch(searchQuery)
  const trackingProps = useQuestionnaireTrackingProps()

  const initialDosageMode: DetailInputMode = medicationToEdit ? resolveDosageMode(medicationToEdit) : 'structured'
  const [dosageMode, setDosageMode] = useState<DetailInputMode>(initialDosageMode)

  const initialValues: MedicationFormValues = medicationToEdit
    ? medicationStatementToFormValues(medicationToEdit)
    : {
        name: '',
        dosageInstructions: '',
        dosageRows: getDefaultDosageRows(),
        asNeededBoolean: false,
      }

  const handleSubmit = (medication: MedicationStatement) => {
    if (dialog.mode === 'update') {
      editMedication(dialog.itemId, medication)
    } else {
      addMedication(medication)
    }
    closeDialog()
  }

  return (
    <Formik
      key={dialog.mode === 'update' ? dialog.itemId : 'new'}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values, { resetForm }) => {
        handleSubmit(formValuesToMedicationStatement(values, dosageMode))
        resetForm()
        setSelectedMedication(null)
      }}
    >
      {({ isValid, isSubmitting, setFieldValue, values }) => (
        <StructuredObservation.ItemFormLayout
          showDetailInput={!!selectedMedication}
          detailMode={dosageMode}
          onDetailModeChange={setDosageMode}
          structuredInput={<MedicationDosageStructuredInput />}
          freeInput={
            <StructuredObservation.FreeInput
              fieldName='dosageInstructions'
              label={t({
                id: 'medication.form.frequency.freeTextLabel',
                message: 'Beschrijf hoeveel en wanneer je je medicatie inneemt',
              })}
              placeholder={t({
                id: 'medication.form.dosageInstructions.placeholder',
                message: 'Bv.: Ik neem elke dag 2 pilletjes bij het ontbijt',
              })}
              minHeight={DOSAGE_INPUT_MIN_HEIGHT}
            />
          }
          submitButton={
            <Button
              type='submit'
              variant='Primary'
              disabled={isMedicationSaveDisabled({
                isSubmitting,
                isValid,
                hasSelectedMedication: !!selectedMedication,
                // Key edit detection on the dialog mode (matches the submit
                // path) rather than whether the item resolved — an update with
                // a stale itemId must still skip the add-only content guards.
                isEdit: dialog.mode === 'update',
                dosageMode,
                dosageRows: values.dosageRows,
                asNeededBoolean: values.asNeededBoolean,
                dosageInstructions: values.dosageInstructions,
              })}
            >
              {medicationToEdit
                ? t({
                    id: 'medication.form.saveButton',
                    message: 'Opslaan',
                  })
                : t({
                    id: 'medication.form.addButton',
                    message: 'Medicatie toevoegen',
                  })}
            </Button>
          }
        >
          {/* Medication name search */}
          <SearchCombobox<MedicationSearchResult>
            id='medication-search'
            label={t({
              id: 'medication.form.search.sectionLabel',
              message: 'Zoek je medicatie',
            })}
            placeholder={t({
              id: 'medication.form.search.placeholder',
              message: 'Zoek geneesmiddel...',
            })}
            selectedName={selectedMedication?.name ?? null}
            results={results}
            getResultName={(r) => r.name}
            freeTextLabel={(query) => (
              <>
                <Span type='BodyMediumBold'>{query}</Span>{' '}
                <Trans id='medication.form.search.addFreeText'>toevoegen als nieuwe medicatie</Trans>
              </>
            )}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            onSelect={(drug) => {
              mixpanel.track('medication_search_result_selected', {
                query_length: searchQuery.length,
                result_rank: results.indexOf(drug) + 1,
                ...trackingProps,
              })
              setSelectedMedication({
                name: drug.name,
                atcCode: drug.atcCode,
                atcName: drug.atcName,
              })
              setFieldValue('name', drug.name)
              setFieldValue('code', drug.atcCode ? { system: 'zorgi-article', value: drug.atcCode } : null)
              const unit = getUnitOptions().find((o) => o.value === drug.dosageUnit)?.value ?? 'pills'
              setFieldValue(
                'dosageRows',
                values.dosageRows.map((row) => ({ ...row, unit })),
              )
            }}
            onSelectFreeText={(name) => {
              mixpanel.track('medication_free_text_selected', {
                query_length: searchQuery.length,
                had_results: results.length > 0,
                ...trackingProps,
              })
              setSelectedMedication({ name })
              setFieldValue('name', name)
              setFieldValue('code', null)
              setFieldValue(
                'dosageRows',
                values.dosageRows.map((row) => ({ ...row, unit: 'pills' })),
              )
            }}
            onClear={() => {
              setSelectedMedication(null)
              setFieldValue('name', '')
              setFieldValue('code', null)
              setFieldValue('dosageRows', getDefaultDosageRows())
            }}
          />
        </StructuredObservation.ItemFormLayout>
      )}
    </Formik>
  )
}
